<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import workflowImage from './assets/clinic-ecrf-workflow.webp'
import {
  ArrowLeft,
  ArrowRight,
  Beaker,
  BookOpenCheck,
  Check,
  ChevronRight,
  ClipboardCheck,
  Download,
  FileJson2,
  FlaskConical,
  FolderOpen,
  Layers3,
  LayoutList,
  LockKeyhole,
  Printer,
  RefreshCcw,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  SquareCheckBig,
  UploadCloud,
  WandSparkles,
  X,
} from 'lucide-vue-next'
import {
  CLINIC_CRF_TEMPLATES,
  deepClone,
  definitionSha256,
  deleteAtPath,
  generateSyntheticData,
  getAtPath,
  hasAtPath,
  parseClinicCrfDocument,
  pruneHiddenData,
  setAtPath,
  validateData,
  verifyPackageIntegrity,
  type ClinicCrfData,
  type ClinicCrfField,
  type ClinicCrfPackageV1,
  type DataValidationResult,
  type ValidationIssue,
} from '@clinic-crf/contract'
import FieldRenderer from './components/FieldRenderer.vue'
import QaPanel from './components/QaPanel.vue'
import {
  MAX_IMPORT_BYTES,
  buildQaReport,
  buildReaderSections,
  integrityLabel,
  safeDownloadName,
  validateImportFile,
  type IntegrityState,
  type ReaderSection,
} from './helpers'

type ViewMode = 'sections' | 'all'
type PrintKind = 'annotated' | 'qa' | null

const activePackage = ref<ClinicCrfPackageV1 | null>(null)
const data = ref<ClinicCrfData>({})
const visited = ref<Set<string>>(new Set())
const touched = ref<Set<string>>(new Set())
const currentSectionIndex = ref(0)
const viewMode = ref<ViewMode>('sections')
const definitionHash = ref('')
const integrity = ref<IntegrityState>('not-provided')
const importIssues = ref<ValidationIssue[]>([])
const importErrors = ref<string[]>([])
const importBusy = ref(false)
const isDragging = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const liveMessage = ref('')
const printKind = ref<PrintKind>(null)

const validation = computed<DataValidationResult>(() => activePackage.value
  ? validateData(activePackage.value.definition, data.value)
  : { errors: {}, fields: [], rules: [], isValid: true })
const fieldEvaluations = computed(() => new Map(validation.value.fields.map((entry) => [entry.field.path, entry])))
const sections = computed<ReaderSection[]>(() => activePackage.value ? buildReaderSections(activePackage.value) : [])
const currentSection = computed(() => sections.value[currentSectionIndex.value] ?? null)
const importWarnings = computed(() => importIssues.value.filter((issue) => issue.severity === 'warning'))
const answeredCount = computed(() => activePackage.value?.definition.fields.filter((field) => hasAtPath(data.value, field.path)).length ?? 0)
const visibleCount = computed(() => validation.value.fields.filter((entry) => entry.visible).length)
const qaReport = computed(() => activePackage.value
  ? buildQaReport(activePackage.value, validation.value, visited.value, definitionHash.value, integrity.value, importIssues.value)
  : null)

const categoryLabels: Record<string, string> = {
  start: 'Start',
  screening: 'Screening',
  visit: 'Wizyta',
  interview: 'Wywiad',
  safety: 'Bezpieczeństwo',
}

const typeLabels: Record<string, string> = {
  string: 'Krótki tekst',
  text: 'Długi tekst',
  integer: 'Liczba całkowita',
  number: 'Liczba dziesiętna',
  boolean: 'Tak / Nie',
  date: 'Data',
  datetime: 'Data i czas',
  choice: 'Pojedynczy wybór',
  multichoice: 'Wielokrotny wybór',
  partial_date: 'Data częściowa',
}

function fieldsForSection(section: ReaderSection): ClinicCrfField[] {
  if (!activePackage.value) return []
  const byPath = new Map(activePackage.value.definition.fields.map((field) => [field.path, field]))
  return section.field_paths.map((path) => byPath.get(path)).filter((field): field is ClinicCrfField => Boolean(field))
}

function visibleFieldsForSection(section: ReaderSection): ClinicCrfField[] {
  return fieldsForSection(section).filter((field) => fieldEvaluations.value.get(field.path)?.visible)
}

function hiddenCountForSection(section: ReaderSection): number {
  return fieldsForSection(section).length - visibleFieldsForSection(section).length
}

function sectionErrorCount(section: ReaderSection): number {
  return fieldsForSection(section).reduce((count, field) => count + (validation.value.errors[field.path]?.length ?? 0), 0)
}

function fieldValue(path: string): unknown {
  return hasAtPath(data.value, path) ? getAtPath(data.value, path) : undefined
}

function presentationFor(path: string) {
  return activePackage.value?.presentation?.fields?.[path]
}

async function openPackage(packageValue: ClinicCrfPackageV1, issues: ValidationIssue[] = [], state?: IntegrityState): Promise<void> {
  activePackage.value = deepClone(packageValue)
  importIssues.value = [...issues]
  importErrors.value = []
  data.value = {}
  visited.value = new Set()
  touched.value = new Set()
  currentSectionIndex.value = 0
  viewMode.value = 'sections'
  integrity.value = state ?? (packageValue.integrity?.definition_sha256 ? 'checking' : 'not-provided')
  definitionHash.value = await definitionSha256(packageValue.definition)
  if (!state && packageValue.integrity?.definition_sha256) {
    integrity.value = (await verifyPackageIntegrity(packageValue)) ? 'verified' : 'mismatch'
  }
  document.title = `${packageValue.form.name} — eCRF Reader`
  liveMessage.value = `Otwarto formularz ${packageValue.form.name}. Odpowiedzi pozostaną wyłącznie w pamięci tej karty.`
  await nextTick()
  markCurrentViewVisited()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function chooseTemplate(id: string): Promise<void> {
  const template = CLINIC_CRF_TEMPLATES.find((candidate) => candidate.id === id)
  if (!template) return
  await openPackage(template.package, [], 'not-provided')
}

async function importFile(file: File | undefined): Promise<void> {
  if (!file) return
  importErrors.value = validateImportFile({ name: file.name, size: file.size, type: file.type })
  importIssues.value = []
  if (importErrors.value.length) return
  importBusy.value = true
  try {
    const source = await file.text()
    const result = parseClinicCrfDocument(source)
    importIssues.value = result.issues
    if (!result.value) {
      importErrors.value = result.issues.filter((issue) => issue.severity === 'error').map((issue) => `${issue.path || 'plik'}: ${issue.message}`)
      return
    }
    const state: IntegrityState = result.value.integrity?.definition_sha256
      ? (await verifyPackageIntegrity(result.value)) ? 'verified' : 'mismatch'
      : 'not-provided'
    if (state === 'mismatch') {
      importErrors.value = ['Import zatrzymany: deklarowana suma SHA-256 nie zgadza się z definicją. Plik mógł zostać zmieniony lub uszkodzony.']
      integrity.value = 'mismatch'
      return
    }
    await openPackage(result.value, result.issues, state)
  } catch (error) {
    importErrors.value = [`Nie udało się odczytać pliku: ${(error as Error).message}`]
  } finally {
    importBusy.value = false
    if (fileInput.value) fileInput.value.value = ''
  }
}

function onFileChange(event: Event): void {
  void importFile((event.target as HTMLInputElement).files?.[0])
}

function onDrop(event: DragEvent): void {
  isDragging.value = false
  void importFile(event.dataTransfer?.files?.[0])
}

function updateField(path: string, value: unknown | undefined): void {
  if (!activePackage.value) return
  const next = deepClone(data.value)
  if (value === undefined) deleteAtPath(next, path)
  else setAtPath(next, path, value)
  const presentBeforePruning = new Set(activePackage.value.definition.fields.filter((field) => hasAtPath(next, field.path)).map((field) => field.path))
  const pruned = pruneHiddenData(activePackage.value.definition, next)
  const removedPaths = Array.from(presentBeforePruning).filter((fieldPath) => !hasAtPath(pruned, fieldPath))
  data.value = pruned
  visited.value = new Set(visited.value).add(path)
  touched.value = new Set(touched.value).add(path)
  if (removedPaths.length) {
    liveMessage.value = `Usunięto ${removedPaths.length} ${removedPaths.length === 1 ? 'ukrytą wartość' : 'ukryte wartości'}: ${removedPaths.join(', ')}.`
  } else {
    liveMessage.value = `Zaktualizowano pole ${path}. Walidacja została przeliczona.`
  }
  void nextTick(markCurrentViewVisited)
}

function touchField(path: string): void {
  visited.value = new Set(visited.value).add(path)
  touched.value = new Set(touched.value).add(path)
}

function markCurrentViewVisited(): void {
  const paths = viewMode.value === 'all'
    ? validation.value.fields.filter((entry) => entry.visible).map((entry) => entry.field.path)
    : currentSection.value
      ? visibleFieldsForSection(currentSection.value).map((field) => field.path)
      : []
  if (!paths.length) return
  const next = new Set(visited.value)
  paths.forEach((path) => next.add(path))
  visited.value = next
}

async function setMode(mode: ViewMode): Promise<void> {
  viewMode.value = mode
  await nextTick()
  markCurrentViewVisited()
  liveMessage.value = mode === 'all' ? 'Wyświetlono wszystkie aktywne pola.' : 'Włączono przejście formularza sekcjami.'
}

async function goToSection(index: number): Promise<void> {
  currentSectionIndex.value = Math.max(0, Math.min(index, sections.value.length - 1))
  await nextTick()
  markCurrentViewVisited()
  document.querySelector<HTMLElement>('#active-section-heading')?.focus()
}

function nextSection(): void {
  if (!currentSection.value) return
  const nextTouched = new Set(touched.value)
  visibleFieldsForSection(currentSection.value).forEach((field) => nextTouched.add(field.path))
  touched.value = nextTouched
  void goToSection(currentSectionIndex.value + 1)
}

function previousSection(): void {
  void goToSection(currentSectionIndex.value - 1)
}

function generateSynthetic(): void {
  if (!activePackage.value) return
  data.value = generateSyntheticData(activePackage.value.definition)
  const visiblePaths = validateData(activePackage.value.definition, data.value).fields.filter((entry) => entry.visible).map((entry) => entry.field.path)
  visited.value = new Set(visiblePaths)
  touched.value = new Set(visiblePaths)
  liveMessage.value = 'Wygenerowano wyłącznie dane syntetyczne i uruchomiono pełną walidację.'
}

function resetTest(ask = true): void {
  if (ask && answeredCount.value && !window.confirm('Wyczyścić wszystkie odpowiedzi testowe z pamięci tej karty?')) return
  data.value = {}
  visited.value = new Set()
  touched.value = new Set()
  currentSectionIndex.value = 0
  liveMessage.value = 'Wyczyszczono odpowiedzi testowe z pamięci.'
  void nextTick(markCurrentViewVisited)
}

function returnToStart(): void {
  if (answeredCount.value && !window.confirm('Zamknąć formularz i usunąć odpowiedzi testowe z pamięci?')) return
  activePackage.value = null
  data.value = {}
  visited.value = new Set()
  touched.value = new Set()
  importIssues.value = []
  definitionHash.value = ''
  document.title = 'eCRF Studio Reader — Sandbox'
  liveMessage.value = 'Wrócono do wyboru formularza.'
}

function exportQaReport(): void {
  if (!qaReport.value || !activePackage.value) return
  const blob = new Blob([`${JSON.stringify(qaReport.value, null, 2)}\n`], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${safeDownloadName(activePackage.value.form.name)}-qa-report.json`
  anchor.click()
  URL.revokeObjectURL(url)
  liveMessage.value = 'Wyeksportowano raport QA bez odpowiedzi testowych.'
}

async function printReport(kind: Exclude<PrintKind, null>): Promise<void> {
  printKind.value = kind
  await nextTick()
  window.print()
  printKind.value = null
}

function fieldConstraints(field: ClinicCrfField): string {
  const parts: string[] = []
  if (field.required) parts.push('zawsze wymagane')
  if (field.required_when) parts.push('warunkowo wymagane')
  if (field.visible_when) parts.push('widoczność warunkowa')
  if (field.min !== undefined || field.max !== undefined) parts.push(`zakres ${field.min ?? '−∞'}–${field.max ?? '+∞'}`)
  if (field.precision !== undefined) parts.push(`precyzja ${field.precision}`)
  if (field.min_length !== undefined || field.max_length !== undefined) parts.push(`długość ${field.min_length ?? 0}–${field.max_length ?? '∞'}`)
  if (field.options?.length) parts.push(`${field.options.length} opcji`)
  if (field.missing_codes?.length) parts.push(`braki: ${field.missing_codes.join(', ')}`)
  return parts.join(' · ') || 'bez dodatkowych ograniczeń'
}
</script>

<template>
  <div class="app-shell">
    <div class="sandbox-banner" role="status">
      <ShieldAlert :size="17" aria-hidden="true" />
      <strong>SANDBOX — WYŁĄCZNIE DANE SYNTETYCZNE</strong>
      <span>Nie wpisuj danych prawdziwego uczestnika. Odpowiedzi nie są zapisywane ani eksportowane.</span>
    </div>

    <header class="site-header">
      <button class="brand" type="button" aria-label="eCRF Studio Reader — strona startowa" @click="activePackage ? returnToStart() : undefined">
        <span class="brand__mark"><SquareCheckBig :size="22" :stroke-width="2.2" aria-hidden="true" /></span>
        <span><b>StrefaBadań</b><small>eCRF Reader / Sandbox</small></span>
      </button>
      <nav class="header-nav" aria-label="Główna nawigacja">
        <span class="local-badge"><LockKeyhole :size="14" aria-hidden="true" /> Local-first</span>
        <a class="header-link" href="https://clinic.strefaankiet.pl" target="_blank" rel="noreferrer">
          Poznaj pełny eCRF <ChevronRight :size="16" aria-hidden="true" />
        </a>
      </nav>
    </header>

    <p class="sr-only" aria-live="polite">{{ liveMessage }}</p>

    <main id="main-content">
      <template v-if="!activePackage">
        <section class="landing-hero">
          <div class="landing-hero__content">
            <span class="eyebrow"><FlaskConical :size="16" aria-hidden="true" /> Otwarty standard clinic-crf</span>
            <h1>Przejdź formularz kliniczny,<br /><em>zanim trafi do badania.</em></h1>
            <p>
              Bezpieczny Reader do przeglądu ścieżek, walidacji i reguł cross-field. Wszystko dzieje się lokalnie w Twojej przeglądarce — bez konta, telemetrii i serwera odpowiedzi.
            </p>
            <div class="hero-actions">
              <button class="button button--primary button--large" type="button" @click="fileInput?.click()">
                <FolderOpen :size="19" aria-hidden="true" /> Otwórz pakiet JSON
              </button>
              <button class="button button--secondary button--large" type="button" @click="chooseTemplate('medical-history')">
                <WandSparkles :size="19" aria-hidden="true" /> Wypróbuj wywiad
              </button>
            </div>
            <div class="trust-row" aria-label="Gwarancje prywatności">
              <span><Check :size="16" aria-hidden="true" /> Bez zapisu odpowiedzi</span>
              <span><Check :size="16" aria-hidden="true" /> Bez połączeń zewnętrznych</span>
              <span><Check :size="16" aria-hidden="true" /> Open source</span>
            </div>
          </div>
          <figure class="hero-preview">
            <div class="workflow-visual-frame">
              <img :src="workflowImage" alt="Zespół badania klinicznego przeglądający cyfrowy obieg danych eCRF" />
            </div>
            <figcaption><span>Kontrola przed publikacją</span><strong>Ścieżki · reguły · walidacja</strong></figcaption>
          </figure>
        </section>

        <section class="import-section" aria-labelledby="import-heading">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Twój formularz</span>
              <h2 id="import-heading">Otwórz i sprawdź pakiet</h2>
              <p>Obsługujemy surowe definicje clinic-crf.v1/v2 oraz pakiety clinic-crf-package.v1.</p>
            </div>
            <span class="limit-note">Maks. {{ MAX_IMPORT_BYTES / 1024 / 1024 }} MB · tylko JSON</span>
          </div>
          <input ref="fileInput" class="sr-only" type="file" accept=".json,application/json" @change="onFileChange" />
          <button
            class="drop-zone"
            :class="{ 'drop-zone--active': isDragging, 'drop-zone--busy': importBusy }"
            type="button"
            :disabled="importBusy"
            @click="fileInput?.click()"
            @dragenter.prevent="isDragging = true"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <UploadCloud :size="34" aria-hidden="true" />
            <span><b>{{ importBusy ? 'Walidujemy pakiet…' : 'Przeciągnij plik tutaj' }}</b><small>lub kliknij, aby wybrać bezpiecznie z dysku</small></span>
            <FileJson2 :size="24" aria-hidden="true" />
          </button>
          <div v-if="importErrors.length" class="import-alert import-alert--error" role="alert">
            <X :size="19" aria-hidden="true" />
            <div><strong>Nie możemy otworzyć tego pliku</strong><ul><li v-for="error in importErrors" :key="error">{{ error }}</li></ul></div>
          </div>
          <div v-if="importIssues.length && !importErrors.length" class="import-alert" role="status">
            <ShieldCheck :size="19" aria-hidden="true" />
            <div><strong>Dokument został rozpoznany</strong><ul><li v-for="issue in importIssues" :key="`${issue.code}-${issue.path}`">{{ issue.message }}</li></ul></div>
          </div>
        </section>

        <section class="templates-section" aria-labelledby="templates-heading">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Gotowe scenariusze</span>
              <h2 id="templates-heading">Zacznij od sprawdzonego wzorca</h2>
              <p>Każdy przykład zawiera wyłącznie syntetyczną treść i od razu uruchamia walidację.</p>
            </div>
          </div>
          <div class="template-grid">
            <button v-for="template in CLINIC_CRF_TEMPLATES" :key="template.id" class="template-card" type="button" @click="chooseTemplate(template.id)">
              <span class="template-card__category">{{ categoryLabels[template.category] }}</span>
              <span class="template-card__icon">
                <Sparkles v-if="template.category === 'start'" :size="22" aria-hidden="true" />
                <ClipboardCheck v-else-if="template.category === 'screening'" :size="22" aria-hidden="true" />
                <Beaker v-else-if="template.category === 'visit'" :size="22" aria-hidden="true" />
                <BookOpenCheck v-else-if="template.category === 'interview'" :size="22" aria-hidden="true" />
                <ShieldAlert v-else :size="22" aria-hidden="true" />
              </span>
              <b>{{ template.title }}</b>
              <span>{{ template.description }}</span>
              <small>{{ template.package.definition.fields.length }} pól · {{ template.package.definition.rules?.length ?? 0 }} reguł <ChevronRight :size="15" /></small>
            </button>
          </div>
        </section>

        <section class="how-section" aria-labelledby="how-heading">
          <div class="section-heading section-heading--center"><div><span class="eyebrow">Prywatność w projekcie</span><h2 id="how-heading">Testujesz formularz, nie dane uczestnika</h2></div></div>
          <div class="how-grid">
            <article><span>01</span><FileJson2 :size="27" /><h3>Wczytaj definicję</h3><p>Plik jest parsowany lokalnie. Reader sprawdza kontrakt, migrację v1 i deklarowaną sumę SHA-256.</p></article>
            <article><span>02</span><Layers3 :size="27" /><h3>Przejdź każdą ścieżkę</h3><p>Branching, wymagania warunkowe i reguły cross-field reagują natychmiast na syntetyczne wartości.</p></article>
            <article><span>03</span><Download :size="27" /><h3>Zabierz raport QA</h3><p>Eksport zawiera statusy, hash i pokrycie testu — nigdy wpisane wartości.</p></article>
          </div>
        </section>

        <section class="product-cta">
          <div><span class="eyebrow">Od projektu do prowadzenia badania</span><h2>Gotowi na kontrolowane środowisko eCRF?</h2><p>Pełna platforma dodaje zespoły, ośrodki, audit trail, queries, review, podpisy i zarządzanie wersjami badania.</p></div>
          <a class="button button--light button--large" href="https://clinic.strefaankiet.pl" target="_blank" rel="noreferrer">Poznaj Clinic eCRF <ArrowRight :size="19" /></a>
        </section>
      </template>

      <template v-else>
        <section class="workspace-topbar">
          <div class="workspace-title">
            <button class="icon-button" type="button" title="Wróć do wyboru formularza" @click="returnToStart"><ArrowLeft :size="19" /><span class="sr-only">Wróć do wyboru formularza</span></button>
            <div><span class="eyebrow">{{ activePackage.form.code }} · wersja {{ activePackage.form.version }}</span><h1>{{ activePackage.form.name }}</h1><p v-if="activePackage.form.description">{{ activePackage.form.description }}</p></div>
          </div>
          <div class="workspace-actions">
            <div class="mode-switch" aria-label="Tryb prezentacji">
              <button type="button" :class="{ active: viewMode === 'sections' }" :aria-pressed="viewMode === 'sections'" @click="setMode('sections')"><Layers3 :size="16" /> Sekcjami</button>
              <button type="button" :class="{ active: viewMode === 'all' }" :aria-pressed="viewMode === 'all'" @click="setMode('all')"><LayoutList :size="16" /> Wszystkie</button>
            </div>
            <button class="button button--secondary button--small" type="button" @click="generateSynthetic"><WandSparkles :size="16" /> Dane syntetyczne</button>
            <button class="button button--ghost button--small" type="button" @click="resetTest()"><RotateCcw :size="16" /> Reset</button>
          </div>
        </section>

        <div v-if="importWarnings.length" class="workspace-warning" role="status">
          <ShieldAlert :size="18" />
          <div><strong>Uwagi z importu</strong><span v-for="warning in importWarnings" :key="`${warning.code}-${warning.path}`">{{ warning.message }}</span></div>
          <button type="button" aria-label="Ukryj uwagi" @click="importIssues = importIssues.filter((issue) => issue.severity !== 'warning')"><X :size="17" /></button>
        </div>

        <section class="workspace-meta" aria-label="Status sandboxa">
          <span><ShieldCheck :size="17" /> {{ integrityLabel(integrity) }}</span>
          <span><FileJson2 :size="17" /> {{ activePackage.definition.version }}</span>
          <span><Layers3 :size="17" /> {{ visibleCount }} aktywnych pól</span>
          <span><RefreshCcw :size="17" /> {{ answeredCount }} wartości tylko w pamięci</span>
          <button type="button" @click="printReport('annotated')"><Printer :size="16" /> Drukuj annotated CRF</button>
        </section>

        <div class="workspace-layout">
          <nav v-if="viewMode === 'sections'" class="section-nav" aria-label="Sekcje formularza">
            <div class="section-nav__heading"><span>Przebieg formularza</span><b>{{ currentSectionIndex + 1 }} / {{ sections.length }}</b></div>
            <ol>
              <li v-for="(section, index) in sections" :key="section.id">
                <button type="button" :class="{ active: index === currentSectionIndex, complete: index < currentSectionIndex }" :aria-current="index === currentSectionIndex ? 'step' : undefined" @click="goToSection(index)">
                  <span class="step-marker"><Check v-if="index < currentSectionIndex" :size="15" /><template v-else>{{ index + 1 }}</template></span>
                  <span><b>{{ section.title }}</b><small>{{ visibleFieldsForSection(section).length }} aktywnych pól</small></span>
                  <em v-if="sectionErrorCount(section)">{{ sectionErrorCount(section) }}</em>
                </button>
              </li>
            </ol>
            <div class="section-nav__privacy"><LockKeyhole :size="17" /><p><b>Sesja ulotna</b><span>Zamknięcie karty usuwa odpowiedzi.</span></p></div>
          </nav>

          <div class="form-canvas">
            <template v-if="viewMode === 'sections' && currentSection">
              <section class="form-section" aria-labelledby="active-section-heading">
                <header class="form-section__header">
                  <div><span class="eyebrow">Sekcja {{ currentSectionIndex + 1 }} z {{ sections.length }}</span><h2 id="active-section-heading" tabindex="-1">{{ currentSection.title }}</h2><p v-if="currentSection.description">{{ currentSection.description }}</p></div>
                  <span v-if="hiddenCountForSection(currentSection)" class="hidden-note">{{ hiddenCountForSection(currentSection) }} {{ hiddenCountForSection(currentSection) === 1 ? 'pole ukryte' : 'pola ukryte' }} przez logikę</span>
                </header>
                <div v-if="visibleFieldsForSection(currentSection).length" class="field-grid">
                  <FieldRenderer
                    v-for="field in visibleFieldsForSection(currentSection)"
                    :key="field.path"
                    :field="field"
                    :value="fieldValue(field.path)"
                    :required="fieldEvaluations.get(field.path)?.required ?? false"
                    :errors="validation.errors[field.path] ?? []"
                    :presentation="presentationFor(field.path)"
                    :show-errors="touched.has(field.path)"
                    @update="updateField(field.path, $event)"
                    @touched="touchField(field.path)"
                  />
                </div>
                <div v-else class="empty-section"><Layers3 :size="28" /><h3>Brak aktywnych pól w tej ścieżce</h3><p>Zmień odpowiedzi w poprzednich sekcjach lub przejdź dalej.</p></div>
                <footer class="section-actions">
                  <button class="button button--secondary" type="button" :disabled="currentSectionIndex === 0" @click="previousSection"><ArrowLeft :size="17" /> Poprzednia</button>
                  <span>{{ currentSectionIndex + 1 }} / {{ sections.length }}</span>
                  <button v-if="currentSectionIndex < sections.length - 1" class="button button--primary" type="button" @click="nextSection">Następna sekcja <ArrowRight :size="17" /></button>
                  <button v-else class="button button--primary" type="button" @click="printReport('qa')">Podsumuj test <ClipboardCheck :size="17" /></button>
                </footer>
              </section>
            </template>

            <template v-else>
              <section v-for="(section, index) in sections" :key="section.id" class="form-section form-section--all" :aria-labelledby="`all-section-${section.id}`">
                <header class="form-section__header"><div><span class="eyebrow">Sekcja {{ index + 1 }}</span><h2 :id="`all-section-${section.id}`">{{ section.title }}</h2><p v-if="section.description">{{ section.description }}</p></div><span v-if="hiddenCountForSection(section)" class="hidden-note">{{ hiddenCountForSection(section) }} ukrytych</span></header>
                <div v-if="visibleFieldsForSection(section).length" class="field-grid">
                  <FieldRenderer
                    v-for="field in visibleFieldsForSection(section)"
                    :key="field.path"
                    :field="field"
                    :value="fieldValue(field.path)"
                    :required="fieldEvaluations.get(field.path)?.required ?? false"
                    :errors="validation.errors[field.path] ?? []"
                    :presentation="presentationFor(field.path)"
                    :show-errors="touched.has(field.path)"
                    @update="updateField(field.path, $event)"
                    @touched="touchField(field.path)"
                  />
                </div>
                <div v-else class="empty-section empty-section--small">Brak aktywnych pól w tej ścieżce.</div>
              </section>
            </template>
          </div>

          <QaPanel :validation="validation" :visited="visited" :hash="definitionHash" :integrity="integrity" @export-report="exportQaReport" @print-report="printReport('qa')" />
        </div>

        <section class="workspace-cta">
          <div><span class="eyebrow">Reader to bezpieczny etap projektowy</span><h2>Potrzebujesz prowadzić prawdziwe badanie?</h2><p>Przenieś zatwierdzoną definicję do platformy z rolami, ośrodkami, audit trail, queries i kontrolą wersji.</p></div>
          <a class="button button--primary button--large" href="https://clinic.strefaankiet.pl" target="_blank" rel="noreferrer">Zobacz pełny Clinic eCRF <ArrowRight :size="19" /></a>
        </section>
      </template>
    </main>

    <footer class="site-footer">
      <div class="brand brand--footer"><span class="brand__mark"><SquareCheckBig :size="20" :stroke-width="2.2" /></span><span><b>StrefaBadań eCRF</b><small>Reader · Open source · MPL-2.0</small></span></div>
      <p>Bez telemetrii, kont użytkowników i przechowywania odpowiedzi. Nie jest systemem do pozyskiwania danych klinicznych.</p>
      <a href="https://clinic.strefaankiet.pl" target="_blank" rel="noreferrer">clinic.strefaankiet.pl <ChevronRight :size="15" /></a>
    </footer>

    <section v-if="printKind === 'annotated' && activePackage" class="print-sheet print-sheet--annotated">
      <header><span>eCRF Studio Reader · Annotated CRF</span><h1>{{ activePackage.form.name }}</h1><p>{{ activePackage.form.code }} · wersja {{ activePackage.form.version }} · {{ activePackage.definition.version }}</p><code>SHA-256: {{ definitionHash }}</code></header>
      <p class="print-warning">Dokument projektowy. Nie zawiera odpowiedzi i nie jest zatwierdzonym formularzem produkcyjnym.</p>
      <section v-for="section in sections" :key="section.id">
        <h2>{{ section.title }}</h2><p v-if="section.description">{{ section.description }}</p>
        <table><thead><tr><th>Ścieżka / etykieta</th><th>Typ i źródło</th><th>Ograniczenia</th></tr></thead><tbody><tr v-for="field in fieldsForSection(section)" :key="field.path"><td><strong>{{ field.label }}</strong><code>{{ field.path }}</code></td><td>{{ typeLabels[field.type] ?? field.type }}<br />{{ field.source ?? 'źródło nieokreślone' }}<template v-if="field.unit"><br />Jednostka: {{ field.unit }}</template></td><td>{{ fieldConstraints(field) }}</td></tr></tbody></table>
      </section>
      <section><h2>Reguły cross-field</h2><p v-if="!activePackage.definition.rules?.length">Brak reguł.</p><ol v-else><li v-for="rule in activePackage.definition.rules" :key="rule.id"><strong>{{ rule.id }}</strong> — {{ rule.message }} <code>{{ rule.path }}</code></li></ol></section>
      <footer>Wygenerowano lokalnie {{ new Date().toLocaleString('pl-PL') }} · eCRF Studio Reader</footer>
    </section>

    <section v-if="printKind === 'qa' && activePackage && qaReport" class="print-sheet print-sheet--qa">
      <header><span>eCRF Studio Reader · Raport QA bez odpowiedzi</span><h1>{{ qaReport.form.name }}</h1><p>{{ qaReport.form.code }} · wersja {{ qaReport.form.version }}</p><code>SHA-256: {{ qaReport.form.definition_sha256 }}</code></header>
      <p class="print-warning">Raport zawiera wyłącznie metadane QA. Nie zawiera wartości testowych.</p>
      <div class="print-metrics"><div><b>{{ qaReport.summary.visited_field_count }}/{{ qaReport.summary.visible_field_count }}</b><span>odwiedzone pola</span></div><div><b>{{ qaReport.summary.error_count }}</b><span>błędy</span></div><div><b>{{ qaReport.summary.passed_active_rule_count }}/{{ qaReport.summary.active_rule_count }}</b><span>aktywne reguły zaliczone</span></div><div><b>{{ qaReport.summary.ready ? 'TAK' : 'NIE' }}</b><span>scenariusz gotowy</span></div></div>
      <h2>Pokrycie pól</h2><table><thead><tr><th>Ścieżka</th><th>Typ</th><th>Status</th><th>Błędy</th></tr></thead><tbody><tr v-for="field in qaReport.fields" :key="field.path"><td><strong>{{ field.label }}</strong><code>{{ field.path }}</code></td><td>{{ field.type }}</td><td>{{ field.status }}</td><td>{{ field.error_count }}</td></tr></tbody></table>
      <h2>Reguły</h2><p v-if="!qaReport.rules.length">Brak reguł cross-field.</p><table v-else><thead><tr><th>ID / ścieżka</th><th>Status</th><th>Komunikat</th></tr></thead><tbody><tr v-for="rule in qaReport.rules" :key="rule.id"><td><strong>{{ rule.id }}</strong><code>{{ rule.path }}</code></td><td>{{ rule.active ? (rule.passed ? 'zaliczona' : 'niezaliczona') : 'nieaktywna' }}</td><td>{{ rule.message }}</td></tr></tbody></table>
      <footer>Wygenerowano {{ new Date(qaReport.generated_at).toLocaleString('pl-PL') }} · {{ integrityLabel(integrity) }}</footer>
    </section>
  </div>
</template>
